// Store delle NOTE
import { create } from 'zustand';
import type { Note, NoteColor } from '@/types';
import { getDatabase } from '@/lib/db';
import { genId, nowISO } from '@/utils';
import { pushNoteToCloud, deleteNoteFromCloud } from './syncHelpers';

interface NotesState {
  notes: Note[];
  loading: boolean;
  search: string;
  setSearch: (s: string) => void;
  load: () => Promise<void>;
  create: (data: Partial<Note>) => Promise<Note>;
  update: (id: string, patch: Partial<Note>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  toggleArchive: (id: string) => Promise<void>;
  replaceAll: (notes: Note[]) => Promise<void>;
}

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: [],
  loading: false,
  search: '',

  setSearch: (s) => set({ search: s }),

  load: async () => {
    set({ loading: true });
    try {
      const db = getDatabase();
      const notes = await db.getAllNotes();
      set({ notes, loading: false });
    } catch (e) {
      console.error('[notes] load error', e);
      set({ loading: false });
    }
  },

  create: async (data) => {
    const now = nowISO();
    const note: Note = {
      id: genId(),
      title: data.title ?? '',
      content: data.content ?? '',
      color: (data.color ?? 'default') as NoteColor,
      tags: data.tags ?? [],
      is_pinned: data.is_pinned ?? false,
      is_archived: data.is_archived ?? false,
      reminder_id: data.reminder_id ?? null,
      created_at: now,
      updated_at: now,
    };
    const db = getDatabase();
    await db.upsertNote(note);
    set({ notes: [note, ...get().notes] });
    pushNoteToCloud(note).catch(() => {});
    return note;
  },

  update: async (id, patch) => {
    const current = get().notes.find((n) => n.id === id);
    if (!current) return;
    const updated: Note = { ...current, ...patch, updated_at: nowISO() };
    const db = getDatabase();
    await db.upsertNote(updated);
    set({ notes: get().notes.map((n) => (n.id === id ? updated : n)) });
    pushNoteToCloud(updated).catch(() => {});
  },

  remove: async (id) => {
    const db = getDatabase();
    await db.deleteNote(id);
    set({ notes: get().notes.filter((n) => n.id !== id) });
    deleteNoteFromCloud(id).catch(() => {});
  },

  togglePin: async (id) => {
    const n = get().notes.find((x) => x.id === id);
    if (n) await get().update(id, { is_pinned: !n.is_pinned });
  },

  toggleArchive: async (id) => {
    const n = get().notes.find((x) => x.id === id);
    if (n) await get().update(id, { is_archived: !n.is_archived });
  },

  replaceAll: async (notes) => {
    const db = getDatabase();
    await db.replaceAllNotes(notes);
    set({ notes: await db.getAllNotes() });
  },
}));
